import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface NavItem {
	id: number;
	name: string;
	children: NavItem[];
	page?: string;
	isClickable?: boolean;
	isTitle?: boolean;
	keepOpen?: boolean;
	isGroupingLabel?: boolean;
	isUserNavigationItem?: boolean;
	isChangeEvent?: boolean;
	isStillSelected?: boolean;
	selectedResource?: string;
	isNameChangeEvent?: boolean;
	isParentChangeEvent?: boolean;
	isActive?: boolean;
	isSuperAdmin?: boolean;
	hasHomePage?: boolean;
	resourceId?: string | number;
	firstname?: string;
	lastname?: string;
	email?: string;
	parentResourceId?: string | number;
	parentName?: string;
	platformId?: string | number;
}

interface NavigationState {
	navState: NavItem[];
	groupChildren: NavItem[];
	roleChildren: NavItem[];
	pluginChildren: NavItem[];
	organizationChildren: NavItem[];
	expandedItems: string[];
	selectedItem: NavItem;
	/** Sidebar drawer: `true` = icon rail, `false` = labels visible. Persisted across reloads. */
	isSidebarNavCollapsed: boolean;
}

const initialState: NavigationState = {
	navState: [
		{ id: 1, name: "RULES", children: [], page: "", isClickable: false, isTitle: true },
		{
			id: 3,
			name: "Library",
			children: [
				{
					id: 4,
					name: "Trash",
					children: [],
					page: "/rules/trash",
					isClickable: true,
					keepOpen: true
				}
			],
			page: "/rules/library",
			isClickable: true,
			keepOpen: true
		},
		{ id: 5, name: "Divider", children: [], page: "", isClickable: false },
		{ id: 6, name: "APP SETTINGS", children: [], page: "", isClickable: false, isTitle: true },
		{ id: 7, name: "Organization", children: [], page: "/settings/organization", isClickable: true },
		{ id: 8, name: "Users", children: [], page: "/settings/users", isClickable: true },
		{ id: 9, name: "Groups", children: [], page: "/settings/groups", isClickable: true },
		{ id: 10, name: "Roles", children: [], page: "/settings/roles", isClickable: true },
		{ id: 11, name: "Workstations", children: [], page: "/settings/workstations", isClickable: true },
		{ id: 12, name: "Plugins", children: [], page: "/settings/plugins", isClickable: true },
		{ id: 13, name: "New Rule Builder", children: [], page: "/new-rule-builder", isClickable: true }
	],
	groupChildren: [
		{ id: 15, name: "Users", page: "", children: [], isClickable: false },
		{ id: 14, name: "Rules", page: "", children: [], isClickable: false },
		{ id: 16, name: "Plugins", page: "", children: [], isClickable: false },
		{ id: 17, name: "Add/Remove IdP Groups", page: "", children: [], isClickable: false }
	],
	roleChildren: [
		{ id: 18, name: "Users", page: "", children: [], isClickable: false },
		{ id: 19, name: "Permissions", page: "", children: [], isClickable: false },
		{ id: 20, name: "Add/Remove IdP Groups", page: "", children: [], isClickable: false }
	],
	pluginChildren: [
		{ id: 21, name: "Groups", page: "", children: [], isClickable: false }
	],
	organizationChildren: [
		{ id: 22, name: "SSO + SCIM Configuration", page: "/settings/organization/sso-scim", isClickable: true, children: [] },
	],
	expandedItems: ["3"],
	selectedItem: {
		id: 3,
		name: "Library",
		children: [
			{
				id: 4,
				name: "Trash",
				children: [],
				page: "/rules/trash",
				isClickable: true,
				keepOpen: true
			}
		],
		page: "/rules/library",
		isClickable: true,
		keepOpen: true
	},
	isSidebarNavCollapsed: false,
};

export const navigationSlice = createSlice({
	name: "navigation",
	initialState,
	reducers: {
		setExpandedItems: (state, action: PayloadAction<string[]>) => { state.expandedItems = action.payload; },
		resetNavigationState: () => initialState,
		setNavState: (state, action: PayloadAction<NavItem[]>) => { state.navState = action.payload; },
		setSelectedItem: (state, action: PayloadAction<NavItem>) => { state.selectedItem = action.payload; },
		setIsSidebarNavCollapsed: (state, action: PayloadAction<boolean>) => {
			state.isSidebarNavCollapsed = action.payload;
		},
	}
});

interface RootState {
	root: {
		navigation: NavigationState;
	};
}

export const selectExpandedItems = (state: RootState) => state.root.navigation.expandedItems;
export const selectGroupChildren = (state: RootState) => state.root.navigation.groupChildren;
export const selectInitialState = (): NavigationState => initialState;
export const selectNavState = (state: RootState) => state.root.navigation.navState;
export const selectPluginChildren = (state: RootState) => state.root.navigation.pluginChildren;
export const selectOrganizationChildren = (state: RootState) => state.root.navigation.organizationChildren;
export const selectRoleChildren = (state: RootState) => state.root.navigation.roleChildren;
export const selectSelectedItem = (state: RootState) => state.root.navigation.selectedItem;
/** Default to expanded so the full CoApp wordmark is visible at first load. */
export const selectIsSidebarNavCollapsed = (state: RootState) =>
	state.root.navigation.isSidebarNavCollapsed ?? false;

export const {
	resetNavigationState,
	setExpandedItems,
	setNavState,
	setSelectedItem,
	setIsSidebarNavCollapsed,
} = navigationSlice.actions;

export default navigationSlice.reducer;
